// 録った音を wav（16 bit PCM・モノラル）にする。**診断専用**（本番では外す。DOC-0026 §1）。
//
// なぜ wav か: 研究用リポジトリの `poc/denoise` の `p27-room-recordings` がこの形をそのまま読めるので、
// 画面で録ったものを DOC-0023 と同じ測り方で測り直せる（`g729_degradation::wav::read_mono16` は
// PCM・モノラル・16 bit を要求する）。圧縮すると騒音のレベルが変わるので使わない。

/** 16 bit PCM・モノラルの wav を組み立てる。`samples` は ±1.0 の範囲。 */
export function toWav(samples: Float32Array, sampleRateHz: number): Blob {
  const bytes = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(bytes);
  const ascii = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, "WAVEfmt ");
  view.setUint32(16, 16, true); // fmt チャンクの大きさ
  view.setUint16(20, 1, true); // 1 = PCM
  view.setUint16(22, 1, true); // モノラル
  view.setUint32(24, sampleRateHz, true);
  view.setUint32(28, sampleRateHz * 2, true); // 1 秒あたりのバイト数
  view.setUint16(32, 2, true); // 1 標本のバイト数
  view.setUint16(34, 16, true); // ビット数
  ascii(36, "data");
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    // 正負で割る数が違う（-1.0 → -32768 / +1.0 → +32767）。同じ数で割ると +1.0 が溢れる
    const v = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Blob([bytes], { type: "audio/wav" });
}

/**
 * 2 乗平均を dBFS（振幅 1.0 を 0 dB とする）で返す。
 *
 * 無音は `-120` に丸める（対数が発散するため）。**区間の指定は `[from, to)`**。
 */
export function rmsDb(samples: Float32Array, from: number, to: number): number {
  const n = Math.max(0, Math.min(to, samples.length) - Math.max(0, from));
  if (n === 0) return -120;
  let sum = 0;
  for (let i = Math.max(0, from); i < Math.min(to, samples.length); i++) {
    const v = samples[i] ?? 0;
    sum += v * v;
  }
  const rms = Math.sqrt(sum / n);
  return rms <= 1e-6 ? -120 : 20 * Math.log10(rms);
}
